/**
 * 📜 Audit Log Service
 *
 * يسجّل كل عملية إدارية مهمة:
 *   - من فعل ماذا، متى، والـ resource المتأثر
 *   - للـ accountability + debugging + compliance
 *
 * Collection: `audit_logs`
 * Read: admin / super_admin فقط (rules)
 *
 * 🛡️ Write: عبر Cloud Function `logAudit` (rules تمنع client write)
 *    الـ Cloud Function تستخدم admin SDK + تحقن actor info موثوقة من token
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit as fbLimit,
  getDocs,
  Timestamp,
} from 'firebase/firestore';

import { db } from '@infrastructure/firebase/config';
import { callLogAudit } from '@infrastructure/firebase/functions';
import { useAuthStore } from '@application/stores/auth.store';

// ─────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────

export type AuditAction =
  // Vendors
  | 'vendor.activate'
  | 'vendor.deactivate'
  | 'vendor.approve'
  | 'vendor.reject'
  // Venues
  | 'venue.delete'
  | 'venue.toggle_status'
  | 'venue.featured_add'
  | 'venue.featured_remove'
  // Bookings / Requests
  | 'booking.force_status'
  | 'booking.manual_create'
  // Reviews
  | 'review.hide'
  | 'review.unhide'
  | 'review.delete'
  // Platform
  | 'platform.maintenance_on'
  | 'platform.maintenance_off'
  | 'platform.force_update'
  | 'platform.commission_change'
  | 'platform.cities_update'
  // Broadcast
  | 'broadcast.send'
  // Data
  | 'data.cleanup'
  | 'data.bulk_action';

export type AuditSeverity = 'info' | 'warning' | 'critical';

export interface AuditLog {
  id: string;
  action: AuditAction;
  severity: AuditSeverity;
  description: string;
  actorUid: string;
  actorName: string;
  actorRole: string;
  resourceType?: string | undefined;
  resourceId?: string | undefined;
  resourceName?: string | undefined;
  metadata?: Record<string, unknown> | undefined;
  timestamp: Date;
}

// ─────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────

class AuditLogService {
  /**
   * ✏️ تسجيل عملية
   *
   * يستدعى من باقي الـ services بعد كل عملية إدارية
   */
  async log(params: {
    action: AuditAction;
    description: string;
    severity?: AuditSeverity;
    resourceType?: string;
    resourceId?: string;
    resourceName?: string;
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    const user = useAuthStore.getState().user;
    if (!user) return; // ما نلوّث لو ما في user

    try {
      // 🛡️ via Cloud Function (admin SDK) — أأمن من client-side write
      //    الـ actor info (uid, email, role) يحقنه الـ Cloud Function
      //    من context.auth (موثوقة — مش من client input)
      await callLogAudit({
        action: params.action,
        description: params.description,
        severity: params.severity ?? 'info',
        ...(params.resourceType !== undefined && { resourceType: params.resourceType }),
        ...(params.resourceId !== undefined && { resourceId: params.resourceId }),
        ...(params.resourceName !== undefined && { resourceName: params.resourceName }),
        ...(params.metadata !== undefined && { metadata: params.metadata }),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[AuditLog] failed to log (continuing action):', err);
      // ما نرمي error — الـ logging shouldn't break the action
    }
  }

  /**
   * 📋 جلب الـ logs (للأدمن)
   */
  async list(filters?: {
    action?: AuditAction;
    actorUid?: string;
    severity?: AuditSeverity;
    limit?: number;
  }): Promise<AuditLog[]> {
    const constraints = [];

    if (filters?.action) {
      constraints.push(where('action', '==', filters.action));
    }
    if (filters?.actorUid) {
      constraints.push(where('actorUid', '==', filters.actorUid));
    }
    if (filters?.severity) {
      constraints.push(where('severity', '==', filters.severity));
    }

    const q = query(
      collection(db, 'audit_logs'),
      ...constraints,
      orderBy('timestamp', 'desc'),
      fbLimit(filters?.limit ?? 100)
    );

    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        action: data['action'] as AuditAction,
        severity: (data['severity'] as AuditSeverity) ?? 'info',
        description: (data['description'] as string) ?? '',
        actorUid: (data['actorUid'] as string) ?? '',
        actorName: (data['actorName'] as string) ?? 'unknown',
        actorRole: (data['actorRole'] as string) ?? '',
        resourceType: (data['resourceType'] as string | undefined) ?? undefined,
        resourceId: (data['resourceId'] as string | undefined) ?? undefined,
        resourceName: (data['resourceName'] as string | undefined) ?? undefined,
        metadata: data['metadata'] as Record<string, unknown> | undefined,
        timestamp: parseDate(data['timestamp']),
      };
    });
  }
}

export const auditLogService = new AuditLogService();

function parseDate(v: unknown): Date {
  if (!v) return new Date();
  if (v instanceof Date) return v;
  if (v instanceof Timestamp) return v.toDate();
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate();
  }
  return new Date();
}
